import { makeAutoObservable, runInAction } from "mobx";
import ApiStore from "./ApiStore";
import { Country } from "../api/CountryApiClient";

export type CountryOption = {
    value: number;
    label: React.ReactNode;
    name: string;
    isoCode: string;
    id: number;
};

const flagUrl = (code: string) => `https://flagcdn.com/${code.toLowerCase()}.svg`;

class CountryStore {
    availableCountries: Country[] = [];
    isLoading: boolean = true;

    private byIso = new Map<string, Country>();
    private byIso3 = new Map<string, Country>();
    private byId = new Map<number, Country>();

    ApiStore!: ApiStore;

    constructor(apiStore: ApiStore) {
        makeAutoObservable(this);
        this.ApiStore = apiStore;
    }

    GetAllAvailableCountries = async () => {
        this.isLoading = true;
        try {
            const items = await this.ApiStore.CountryApiClient.GetAvailableCountries();
            runInAction(() => {
                this.availableCountries = items;
                this.byIso.clear();
                this.byId.clear();
                for (const c of items) {
                    this.byIso.set(c.isoCode, c);
                    this.byIso3.set(c.iso3Code, c);    
                    this.byId.set(c.id, c);
                }
            });
        } catch (error) {
            console.error("Error fetching available countries:", error);
        } finally {
            runInAction(() => { this.isLoading = false; });
        }
    };

    getByIso = (isoCode: string) => this.byIso.get(isoCode) ?? null;
    getByIso3 = (iso3Code: string) => this.byIso3.get(iso3Code) ?? null;
    getById = (id: number) => this.byId.get(id) ?? null;
    getByAnyIso = (code: string) => this.byIso.get(code) ?? this.byIso3.get(code) ?? null;

    isoToId = (isoCode: string) => this.byIso.get(isoCode)?.id ?? null;
    iso3ToId = (iso3Code: string) => this.byIso3.get(iso3Code)?.id ?? null;
    idToIso = (id: number) => this.byId.get(id)?.isoCode ?? null;

    private buildLabel(name: string, iso: string, withFlags: boolean): React.ReactNode {
        if (!withFlags) return name;
        return (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }
            }>
                <img
                    src={flagUrl(iso)}
                    alt={name}
                    width={24}
                    height={18}
                    loading="lazy"
                    style={{ objectFit: "cover", borderRadius: 3, boxShadow: "2px 2px 2px 0 rgba(0,0,0,0.06)" }}
                    onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                />
                < span > {name} </span>
            </span>
        );
    }

    private getDisplayName(iso: string, serverName: string, lang: string) {
        const locale = (lang || "en").replace("_", "-");
        try {
            const AnyIntl = Intl as any;
            if (AnyIntl?.DisplayNames) {
                const dn = new AnyIntl.DisplayNames([locale], { type: "region" });
                const locName = dn?.of(iso);
                if (locName && typeof locName === "string") return locName;
            }
        } catch { }
        return serverName;
    }

    toOptions = (lang: string, opts?: { withFlags?: boolean }): CountryOption[] => {
        const withFlags = opts?.withFlags ?? false;
        const locale = (lang || "en").replace("_", "-");

        const options: CountryOption[] = this.availableCountries.map(c => {
            const name = this.getDisplayName(c.isoCode, c.name, lang);
            return {
                value: c.id,
                label: this.buildLabel(name, c.isoCode, withFlags),
                name,
                isoCode: c.isoCode,
                id: c.id,
            };
        });

        return options.sort((a, b) => a.name.localeCompare(b.name, locale));
    };

    get options(): CountryOption[] {
        return this.toOptions("en", { withFlags: false });
    }
}

export default CountryStore;
